import React from "react";
import { Grid, Card } from "@mui/material";
import { LineChart, Legend, Line, Tooltip, CartesianGrid, YAxis, XAxis } from "recharts";
import { useTranslate } from "src/locales";
import { formatTime, formatDateTime } from "./utils";

interface PartographChartsProps {
    serviksSizeData: any[];
    fetalHeartRateData: any[];
    headLevelData: any[];
    fontSize: number;
}

const PartographCharts: React.FC<PartographChartsProps> = ({
    serviksSizeData,
    fetalHeartRateData,
    headLevelData,
    fontSize,
}) => {
    const { t } = useTranslate();

    return (
        <Card sx={{ marginBottom: "5px" }}>
            <Grid
                container
                sx={{
                    m: 1,
                    width: "calc(100% - 16px)",
                    p: 2,
                    borderBottom: "1px solid #E0E0E0",
                    backgroundColor: "#63B4FF33",
                    borderRadius: "10px",
                }}
            >
                <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                    {t("partograf-form.graphs")}
                </span>
            </Grid>
            <Grid item sm={12} xs={12} md={12} xl={12} container>
                <Grid item sm={12} xs={12} md={6} xl={6} container>
                    <LineChart
                        width={500}
                        height={300}
                        data={serviksSizeData}
                        margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="2 2" />
                        <YAxis domain={[0, 10]} ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]} />
                        <XAxis
                            dataKey="PROCESS_TIME"
                            tickFormatter={(unixTime) => (unixTime ? formatTime(unixTime) : "")}
                            type="category"
                        />
                        <Legend
                            iconType="circle"
                            iconSize={6}
                            verticalAlign="top"
                            layout="horizontal"
                            align="center"
                            wrapperStyle={{
                                position: "absolute",
                                top: "10px",
                                left: "55%",
                                transform: "translateX(-50%)",
                                zIndex: 10,
                                fontSize: fontSize,
                            }}
                        />
                        <Tooltip labelFormatter={(v) => formatDateTime(v)} />
                        <Line
                            type="monotone"
                            dataKey="SERVIKS_SIZE"
                            name={t("partograf-form.cervix-dilation")}
                            stroke="#F9C74F"
                            strokeWidth={5}
                            dot={{ r: 6 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="WARNING_AREA"
                            name={t("partograf-form.warning-interval")}
                            stroke="#0A8FDC"
                            strokeWidth={5}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="INTERVENTION_AREA"
                            name={t("partograf-form.Intervention-range")}
                            stroke="#43B02A"
                            strokeWidth={5}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="MEDIAN_LINE"
                            name="-"
                            stroke="#E74C3C"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={false}
                        />
                    </LineChart>
                </Grid>
                <Grid item sm={12} xs={12} md={6} xl={6} container>
                    <LineChart
                        width={500}
                        height={300}
                        data={fetalHeartRateData}
                        margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="2 2" />
                        <YAxis domain={[60, 200]} ticks={[60, 80, 100, 120, 140, 160, 180, 200]} />
                        <XAxis
                            dataKey="PROCESS_TIME"
                            tickFormatter={(unixTime) => (unixTime ? formatTime(unixTime) : "")}
                            type="category"
                        />
                        <Legend
                            iconType="circle"
                            iconSize={6}
                            verticalAlign="top"
                            layout="horizontal"
                            align="center"
                            wrapperStyle={{
                                position: "absolute",
                                top: "10px",
                                left: "55%",
                                transform: "translateX(-50%)",
                                zIndex: 10,
                                fontSize: fontSize,
                            }}
                        />
                        <Tooltip labelFormatter={(v) => formatDateTime(v)} />

                        <Line
                            type="monotone"
                            dataKey="FETAL_HEART_RATE"
                            name={t("partograf-form.fetal-heart-rate")}
                            stroke="#FFC160"
                            strokeWidth={4}
                            dot={{ r: 4 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="MEDIAN_LINE_1"
                            name="-"
                            stroke="#E74C3C"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="MEDIAN_LINE_2"
                            name="-"
                            stroke="#E74C3C"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={false}
                        />
                    </LineChart>
                </Grid>

                <Grid item sm={12} xs={12} md={6} xl={6} container>
                    <LineChart
                        width={500}
                        height={300}
                        data={headLevelData}
                        margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="1 1" />
                        <YAxis domain={[-3, 3]} ticks={[-3, -2, -1, 0, 1, 2, 3]} />
                        <XAxis
                            dataKey="PROCESS_TIME"
                            tickFormatter={(unixTime) => (unixTime ? formatTime(unixTime) : "")}
                            type="category"
                        />
                        <Legend
                            iconType="circle"
                            iconSize={6}
                            verticalAlign="top"
                            layout="horizontal"
                            align="center"
                            wrapperStyle={{
                                position: "absolute",
                                top: "10px",
                                left: "55%",
                                transform: "translateX(-50%)",
                                zIndex: 10,
                                fontSize: fontSize,
                            }}
                        />
                        <Tooltip labelFormatter={(v) => formatDateTime(v)} />

                        <Line
                            type="monotone"
                            dataKey="HEAD_LEVEL"
                            name={t("partograf-form.head-level")}
                            stroke="#E74C3C"
                            strokeWidth={4}
                            dot={{ r: 4 }}
                        />
                    </LineChart>
                </Grid>
            </Grid>
        </Card>
    );
};

export default PartographCharts;
